import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2,
  Utensils,
  ArrowLeft,
  Calendar,
  Clock,
  Check,
  Car,
} from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FBF7F0]" data-testid="manage-page">
    <div className="noise-overlay" aria-hidden="true" />
    <header className="border-b border-[#8E1226]/10">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 text-[#8E1226]">
          <Utensils className="w-5 h-5 text-[#0E6B3A]" strokeWidth={1.6} />
          <span className="text-sm font-medium">
            Meals on Wheels <span className="opacity-60">Tralee</span>
          </span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6D726A] hover:text-[#0E6B3A] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </div>
    </header>
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-12 md:py-16">
      {children}
    </div>
  </div>
);

const SlotPicker = ({ days, currentShiftId, currentDayId, onPick, busyId }) => {
  const [active, setActive] = useState(currentDayId || days[0]?.id);
  return (
    <Tabs value={active} onValueChange={setActive} className="w-full">
      <TabsList className="bg-transparent p-0 h-auto gap-3 mb-10 flex-wrap justify-start">
        {days.map((d) => (
          <TabsTrigger
            key={d.id}
            value={d.id}
            data-testid={`manage-day-tab-${d.id}`}
            className="rounded-full border border-[#8E1226]/15 px-6 py-3 text-sm data-[state=active]:bg-[#8E1226] data-[state=active]:text-[#FBF7F0] data-[state=inactive]:text-[#8E1226] transition-colors"
          >
            {d.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {days.map((d) => (
        <TabsContent key={d.id} value={d.id} className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {d.roles.map((role) => {
              const isDriver = /driver/i.test(role.role);
              return (
                <div
                  key={role.role}
                  className="rounded-2xl border border-[#8E1226]/12 bg-[#FBF7F0] p-6"
                >
                  <div className="flex items-start gap-2">
                    <h4 className="font-serif text-xl leading-tight text-[#8E1226]">
                      {role.role}
                    </h4>
                    {isDriver && (
                      <Car className="w-4 h-4 text-[#6D726A] shrink-0 mt-1" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    {role.slots.map((slot) => {
                      const current = slot.id === currentShiftId;
                      const full = slot.remaining <= 0 && !current;
                      const disabled = full || current || !!busyId;
                      return (
                        <button
                          key={slot.id}
                          data-testid={`manage-slot-${slot.id}`}
                          disabled={disabled}
                          onClick={() => onPick(slot.id)}
                          className={`flex items-center justify-between rounded-xl border px-4 py-2.5 text-sm transition-all duration-300 ${
                            current
                              ? "border-[#0E6B3A] bg-[#0E6B3A]/10 text-[#8E1226] cursor-default"
                              : full
                              ? "border-[#8E1226]/10 text-[#6D726A] cursor-not-allowed bg-[#F2E8D5]/40"
                              : "border-[#8E1226]/15 text-[#8E1226] hover:border-[#0E6B3A] hover:bg-[#0E6B3A] hover:text-[#FBF7F0]"
                          }`}
                        >
                          <span className="font-medium">
                            {slot.start} – {slot.end}
                          </span>
                          <span className="text-xs">
                            {busyId === slot.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : current ? (
                              "Current"
                            ) : full ? (
                              "Full"
                            ) : (
                              `${slot.remaining} left`
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default function Manage() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("view");
  const [busyId, setBusyId] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get(`/manage/${token}`),
        api.get(`/shifts`),
      ]);
      setBooking(b.data.signup);
      setDays(s.data.days);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error("Something went wrong loading your booking.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pickSlot = async (shiftId) => {
    setBusyId(shiftId);
    try {
      const { data } = await api.put(`/manage/${token}`, { shift_id: shiftId });
      setBooking(data.signup);
      setMode("view");
      toast.success("Your shift has been updated. A new confirmation is on its way.");
      const s = await api.get(`/shifts`);
      setDays(s.data.days);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.delete(`/manage/${token}`);
      setCancelled(true);
      toast.success("Your shift has been cancelled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center gap-3 text-[#6D726A]">
          <Loader2 className="w-6 h-6 animate-spin" /> Loading your booking…
        </div>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <h1 className="font-serif font-light text-4xl md:text-5xl text-[#8E1226]">
          We couldn't find that booking.
        </h1>
        <p className="mt-4 text-[#6D726A] max-w-md">
          The link may be out of date or the shift may have been cancelled. You
          can sign up again on the home page.
        </p>
        <Link
          to="/"
          className="inline-block mt-8 rounded-full bg-[#8E1226] text-[#FBF7F0] px-7 py-3 text-sm font-medium hover:bg-[#0E6B3A] transition-colors"
        >
          Back to sign-up
        </Link>
      </Shell>
    );
  }

  if (cancelled) {
    return (
      <Shell>
        <div className="max-w-md" data-testid="manage-cancelled">
          <div className="w-14 h-14 rounded-full bg-[#8E1226] flex items-center justify-center mb-6">
            <Check className="w-7 h-7 text-[#0E6B3A]" strokeWidth={2} />
          </div>
          <h1 className="font-serif font-light text-4xl md:text-5xl text-[#8E1226]">
            Your shift is cancelled.
          </h1>
          <p className="mt-4 text-[#6D726A]">
            Thanks for letting us know — your spot has been freed up for someone
            else. You're always welcome to sign up again.
          </p>
          <Link
            to="/"
            className="inline-block mt-8 rounded-full bg-[#8E1226] text-[#FBF7F0] px-7 py-3 text-sm font-medium hover:bg-[#0E6B3A] transition-colors"
          >
            Sign up again
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <span className="text-xs uppercase tracking-[0.32em] text-[#0E6B3A]">
        Manage your shift
      </span>
      <h1 className="mt-5 font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight text-[#8E1226]">
        Hi {booking?.name?.split(" ")[0] || "there"}.
      </h1>

      <div
        data-testid="manage-current-booking"
        className="mt-10 rounded-2xl border border-[#8E1226]/12 bg-[#FBF7F0] p-7 md:p-9 max-w-2xl"
      >
        <div className="text-xs uppercase tracking-[0.2em] text-[#6D726A] mb-4">
          Your current booking
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-[#8E1226] tracking-tight">
          {booking?.role}
        </h2>
        <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-[#8E1226]">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#0E6B3A]" strokeWidth={1.6} />
            {booking?.day_label} · {booking?.weekday} {booking?.date}
          </span>
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#0E6B3A]" strokeWidth={1.6} />
            {booking?.start} – {booking?.end}
          </span>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {mode === "view" ? (
            <>
              <button
                data-testid="manage-switch-btn"
                onClick={() => setMode("switch")}
                className="rounded-full bg-[#8E1226] text-[#FBF7F0] px-7 py-3 text-sm font-medium hover:bg-[#0E6B3A] transition-colors"
              >
                Change my shift
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid="manage-cancel-btn"
                    className="rounded-full border border-[#8E1226]/20 text-[#8E1226] px-7 py-3 text-sm font-medium hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors"
                  >
                    Cancel my shift
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent
                  data-testid="cancel-confirm-dialog"
                  className="bg-[#FBF7F0] border-[#8E1226]/10"
                >
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-serif text-2xl text-[#8E1226]">
                      Cancel this shift?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-[#6D726A]">
                      This frees up your spot for someone else. You can always
                      sign up again afterwards.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">
                      Keep my shift
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="cancel-confirm-btn"
                      onClick={cancel}
                      disabled={cancelling}
                      className="rounded-full bg-[#0E6B3A] hover:bg-[#083D21] text-[#FBF7F0]"
                    >
                      {cancelling && (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      )}
                      Yes, cancel
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <button
              data-testid="manage-switch-cancel-btn"
              onClick={() => setMode("view")}
              className="rounded-full border border-[#8E1226]/20 text-[#8E1226] px-7 py-3 text-sm font-medium hover:border-[#0E6B3A] transition-colors"
            >
              ← Keep my current shift
            </button>
          )}
        </div>
      </div>

      {mode === "switch" && (
        <div className="mt-14" data-testid="manage-switch-picker">
          <h3 className="font-serif font-light text-3xl md:text-4xl text-[#8E1226] mb-3">
            Choose a new day, role or time.
          </h3>
          <p className="text-[#6D726A] mb-10 max-w-xl">
            Pick any available slot below — we'll move you across and email an
            updated confirmation. Full slots are greyed out.
          </p>
          <SlotPicker
            days={days}
            currentShiftId={booking?.shift_id}
            currentDayId={booking?.day_id}
            onPick={pickSlot}
            busyId={busyId}
          />
        </div>
      )}
    </Shell>
  );
}
